/* eslint-disable react/no-array-index-key */
import {
  useForm,
  ComponentType,
  IconName,
  Icon,
  convertDateToString,
  convertISOStringToDate,
  convertISOStringToLocalDateString,
  Form,
  Scrollbar,
  IBreadcrumbItem,
  Badge,
  Accordion,
  ToastMessage,
  toastMessage,
} from '@gcl/react-core';
import {
  useSubmitFormEnabled as useTransmittalItemTableSubmitFormEnabled,
  useFormPrefix,
  useSubmitRow,
  useFocus,
} from './transmittalItemTableForm/transmittalItemTableForm.hooks';

import { routes } from '@routes/routes.models';
import * as React from 'react';
import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { DetailPageRightMenu } from '@common/detailPageRightMenu/DetailPageRightMenu';
import { EDetailPageRightMenuGeneralInfoType } from '@common/detailPageRightMenu/detailPageRightMenuGeneralInfo.models';
import { Tab, Tabs } from '@core/Tabs/Tab';

import {
  useCreateDraftProjectTransmittalIfNotExist,
  useDeleteProjectTransmittal,
  useEndorseProjectTransmittal,
  useFetchLatestTransmittalByMasterScheduleId,
  useFetchTransmittalById,
  useFetchTransmittalDistribution,
  useReleaseForConstructionProjectTransmittal,
  useRequestEndorseProjectTransmittal,
  useUpdateProjectTransmittal,
} from '@transmittal/transmittal.queries';
import './TransmittalDetailPage.scss';
import { getAccessMode } from '@common/detailPage.hooks';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { Note } from '@core/Note/Note';
import { INoteItem } from '@core/Note/NoteList';
import { ITagViewItem, TagView } from '@masterSchedule/detail/tagView/TagView';
import { ITag } from '@tag/tag.models';
import { useLocalTags } from '@masterSchedule/detail/tagView/tag.hooks';
import { ETransmittalStatus, ETransmittalType, TransmittalTypeOptions } from '@transmittal/transmittal.constants';
import {
  ICreateProjectTransmittalRequest,
  IProjectTransmittalDetail,
  ITransmittalTypeOption,
  IUpdateProjectTransmittalRequest,
  IUpsertProjectTransmittalDesignModification,
} from '@transmittal/transmittal.models';
import { useGetTransmittalPurposeOption } from '@transmittal/transmittalPurpose/transmittalPurpose.queries';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME, MUTATION_KEYS, PURPOSE_CODE_Z } from '@common/constants';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { useFetchAttachmentTransmittal } from '@file/projectFile/projectFile.queries';
import { DownloadButton } from '@file/DownloadButton';
import { exportTransmittalDetailExcelAsync, exportTransmittalDetailPdfAsync } from '@transmittal/transmittal.services';
import { ITransmittalPurpose } from '@transmittal/transmittalPurpose/transmittalPurpose.models';
import { Attachments } from '@file/Attachments';
import { DiscussionList } from '@discussion/DiscussionList';
import { EProjectDiscussionFormType } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { IFormFileDrawingItem, IProjectFileAttachmentsTransmittalsRequest } from '@file/projectFile/projectFile.models';
import usePrompt from '@common/prompt.hooks';
import { useFetchDesignModificationOptionForDesignerTransmittal } from '@designModification/designModification.queries';
import { IProjectDesignModificationOptionForDesignerTransmittal } from '@designModification/designModification.models';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { IUserProfileOption } from '@userProfile/userProfile.models';
import { useDeferLoading } from '@common/loading.hooks';
import { useFetchAllTransmittalItemDocumentTypes } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.queries';
import { useFetchCheckerTransmittalsByT1 } from '@common/detailPageRightMenu/DetailPageRightMenu.queries';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { ETransmittalPurposeCodeConst } from '@transmittal/transmittalPurpose/transmittalPurpose.constants';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { TransmittalDetailTabDetail } from './TransmittalDetailTabDetail';
import { ITransmittalDetailsFormState } from './transmittalDetailPage.models';
import {
  getLambethReviewerResponseStatusComponentType,
  getTransmittalItemRoute,
  useDefaultValue,
  useHasCommentAndResponseRight,
  useShouldShowRemovedFrom,
  useTransmittalDetailPageConfirmationModal,
  useTransmittalSupplementaryInfo,
  useTransmittalTopBtnsVisibility,
} from './transmittalDetailPage.hooks';
import { TransmittalDetailTabReviewResponse } from './TransmittalDetailTabReviewResponse';
import { ETransmittalConfirmationActionType, ETransmittalDetailsFormKeys } from './transmittalDetailPage.constants';
import {
  ITransmittalItemRowEditingEvents,
  ITransmittalItemTableEditingEvents,
  ITransmittalItemTableEditingRef,
} from './transmittalItemTableForm/transmittalItemTableForm.models';

interface ITransmittalDetailPageProps {}

export const TransmittalDetailPage: React.FunctionComponent<ITransmittalDetailPageProps> = ({}) => {
  const { isXs } = useBreakPoints();
  const isProjectArchived = useProjectIsArchived();

  const { getTablePrefix, getRowPrefix, getRowFormPrefix } = useFormPrefix();

  const isWindowFocus = useFocus();

  const projectId = useProjectId();
  const { id, mode } = getAccessMode();

  const { shouldShowRemovedFromUse, setShouldShowRemovedFromUse } = useShouldShowRemovedFrom();

  const [isPageEditing, setIsPageEditing] = React.useState(false);
  const [isPageUploading, setIsPageUploading] = React.useState(false);
  const refTableIsEditing = React.useRef<boolean[]>([]);
  const refTableIsUploading = React.useRef<boolean[]>([]);
  const transmittalItemTableEditingRef = React.useRef<ITransmittalItemTableEditingRef>({});

  const isMutatingUpsertTransmittalItems = useIsMutating([MUTATION_KEYS.UPSERT_PROJECT_TRANSMITTAL_ITEM_REVISION]);

  const [isAnyIceDesignCertified, setIsAnyIceDesignCertified] = React.useState(false);
  const refTableIsIceDesignCertified = React.useRef<boolean[]>([]);

  const setTableIsIceDesignCertified = (index: number, isIceDesignCertified: boolean) => {
    const temp = [...refTableIsIceDesignCertified.current];
    temp[index] = isIceDesignCertified;
    refTableIsIceDesignCertified.current = temp;

    const isAnyTableIsIceDesignCertified = temp.find((item) => item === true) ?? false;
    setIsAnyIceDesignCertified(isAnyTableIsIceDesignCertified);
  };

  const setRowTableIsEditing = (index: number, isEditing: boolean) => {
    const temp = [...refTableIsEditing.current];
    temp[index] = isEditing;
    refTableIsEditing.current = temp;

    const isAnyTableEditing = temp.find((item) => item === true) ?? false;
    setIsPageEditing(isAnyTableEditing);
  };

  const setRowTableIsUploading = (index: number, isUploading: boolean) => {
    const temp = [...refTableIsUploading.current];
    temp[index] = isUploading;
    refTableIsUploading.current = temp;

    const isAnyTableUploading = temp.find((item) => item === true) ?? false;
    setIsPageUploading(isAnyTableUploading);
  };

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const { setDefaultValues, defaultValues } = useDefaultValue(useFormMethods);

  const {
    formState: { isDirty },
  } = useFormMethods;

  const isPageDirty = React.useMemo(() => {
    return isDirty && Object.keys(useFormMethods.formState.dirtyFields).length > 0;
  }, [isDirty, JSON.stringify(useFormMethods.formState.dirtyFields)]);
  const {
    data: transmittalDetails,
    isLoading: isLoadingTransmittalDetails,
    isRefetching: isRefetchingTransmittalDetails,
    error: errorGetTransmittalDetails,
  } = useFetchTransmittalById({ projectId }, id, !isDirty);
  const {
    masterSchedule,
    isInitialLoading: isLoadingGetMasterSchedule,
    isRefetching: isRefetchingGetMasterSchedule,
    error: errorGetMasterSchedule,
  } = getMasterScheduleDetail(projectId, transmittalDetails?.masterScheduleId, !isDirty);
  const { data: latestTransmittal, error: errorLatestTransmittal } = useFetchLatestTransmittalByMasterScheduleId(
    {
      projectId,
      masterScheduleId: transmittalDetails?.masterScheduleId || 0,
      transmittalType: transmittalDetails?.type || undefined,
    },
    transmittalDetails?.status === ETransmittalStatus.APPROVED,
  );

  const {
    data: transmittalItemDocumentTypes,
    isLoading: isLoadingTransmittalItemDocumentTypes,
    isRefetching: isRefetchingTransmittalItemDocumentTypes,
    error: errorTransmittalItemDocumentTypes,
  } = useFetchAllTransmittalItemDocumentTypes();

  const {
    data: distributionData,
    isLoading: isLoadingDistribution,
    isRefetching: isRefetchingDistribution,
    error: errorDistribution,
  } = useFetchTransmittalDistribution({ projectId }, id);

  const { data: designModificationOptions, error: errorDesignModificationOptions } =
    useFetchDesignModificationOptionForDesignerTransmittal(
      {
        projectId,
        masterScheduleId: transmittalDetails?.masterScheduleId ?? 0,
      },
      transmittalDetails?.type === ETransmittalType.DESIGNER && transmittalDetails.status === ETransmittalStatus.DRAFT,
    );

  const {
    data: dataCheckerTransmittalsByT1,
    isLoading: isLoadingCheckerTransmittalsByT1,
    isRefetching: isRefetchingCheckerTransmittalsByT1,
    error: errorCheckerTransmittalsByT1,
  } = useFetchCheckerTransmittalsByT1(
    { projectId, projectMasterScheduleId: transmittalDetails?.masterScheduleId ?? -1 },
    false,
  );

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorGetTransmittalDetails]);
  }, [JSON.stringify(errorGetTransmittalDetails)]);

  const purposeCode: string = useFormMethods.getValues(ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE)?.code;
  const topBtnsVisibility = useTransmittalTopBtnsVisibility(
    useFormMethods,
    projectId,
    transmittalDetails,
    masterSchedule,
    purposeCode,
    transmittalItemDocumentTypes,
  );
  const { isSubmitEnabled: isTransmittalItemTableSubmitFormEnabled } = useTransmittalItemTableSubmitFormEnabled(
    useFormMethods,
    undefined,
  );

  const {
    isEnableEndorseTransmittal,
    isEnableRequestEndorsement,
    isEnableReleaseForConstruction,
    isEnablePrepareApproval,
    isEnablePrepareReview,
    isEnableDeleteTransmittal,
    isEnableSaveTransmittal,
  } = topBtnsVisibility;

  const isSuperseded = React.useMemo(() => {
    if (!transmittalDetails || !latestTransmittal || transmittalDetails?.status !== ETransmittalStatus.APPROVED)
      return false;
    return transmittalDetails.id !== latestTransmittal.id;
  }, [JSON.stringify(transmittalDetails), JSON.stringify(latestTransmittal)]);

  const { canCreateComment, canReplyComment, canViewComment, canViewDraftComment } = useHasCommentAndResponseRight(
    transmittalDetails,
    masterSchedule?.responsibleChecker.id,
    isSuperseded,
    masterSchedule,
  );

  const isReadOnly = React.useMemo(() => {
    return (
      mode === 'View' ||
      transmittalDetails?.status !== ETransmittalStatus.DRAFT ||
      (transmittalDetails.isArchived ?? false) ||
      isProjectArchived ||
      (masterSchedule?.isLocked ?? false) ||
      (masterSchedule?.isCancelled ?? false)
    );
  }, [mode, transmittalDetails, isProjectArchived]);

  const [typeOption, setTypeOption] = React.useState<ITransmittalTypeOption | undefined>();
  const transmittalName = React.useMemo(() => {
    if (!typeOption) return 'Transmittal';
    return typeOption.formName;
  }, [typeOption]);

  const { mode: supplementaryMode, fileType: supplementaryFileType } =
    useTransmittalSupplementaryInfo(transmittalDetails);

  const { navigate, projectNo } = useNavigate();
  const queryClient = useQueryClient();

  const { renderConfirmationPortal, openConfirmationPortal, setConfirmationActionType, confirmationActionType } =
    useTransmittalDetailPageConfirmationModal(
      async (confirmationActionTypeLocal?: ETransmittalConfirmationActionType) => {
        switch (confirmationActionTypeLocal) {
          case ETransmittalConfirmationActionType.save: {
            const result = await onSaveTransmittalItems();
            if (result === true) useFormMethods.handleSubmit(handleSubmit('save'))();
            break;
          }
          case ETransmittalConfirmationActionType.delete:
            mutateDeleteTransmittal({ id, masterScheduleId: transmittalDetails?.masterScheduleId, projectId });
            break;
          case ETransmittalConfirmationActionType.requestEndorsement:
            useFormMethods.handleSubmit(handleSubmit('requestEndorsement'))();
            break;
          case ETransmittalConfirmationActionType.endorse: {
            const result = await onSaveTransmittalItems();
            if (result === true) useFormMethods.handleSubmit(handleSubmit('endorsement'))();
            break;
          }
          case ETransmittalConfirmationActionType.releaseForConstruction:
            useFormMethods.handleSubmit(handleSubmit('releaseForConstruction'))();
            break;
          case ETransmittalConfirmationActionType.signAndSend:
            useFormMethods.handleSubmit(handleSubmit('endorsement'))();
            break;
          case ETransmittalConfirmationActionType.prepareApproval:
            onClickPrepareApprovalOrReview(ETransmittalType.ICE);
            break;
          case ETransmittalConfirmationActionType.prepareReview:
            onClickPrepareApprovalOrReview(ETransmittalType.REVIEWER);
            break;
          default:
            break;
        }
      },
    );

  const {
    mutate: mutateUpdateTransmittal,
    data: dataUpdateTransmittal,
    error: errorUpdateTransmittal,
    reset: resetUpdateTransmittal,
    isLoading: isLoadingUpdateTransmittal,
    isError: isErrorUpdateTransmittal,
    isSuccess: isSuccessUpdateTransmittal,
  } = useUpdateProjectTransmittal();

  const {
    mutate: mutateDeleteTransmittal,
    isSuccess: isSuccessDeleteTransmittal,
    isLoading: isLoadingDeleteTransmittal,
    isError: isErrorDeleteTransmittal,
    error: errorDeleteTransmittal,
    reset: resetDeleteTransmittal,
  } = useDeleteProjectTransmittal();

  const {
    mutate: mutateEndorseTransmittal,
    isSuccess: isSuccessEndorseTransmittal,
    isLoading: isLoadingEndorseTransmittal,
    isError: isErrorEndorseTransmittal,
    error: errorEndorseTransmittal,
    reset: resetEndorseTransmittal,
  } = useEndorseProjectTransmittal();

  const {
    mutate: mutateRequestEndorseTransmittal,
    isSuccess: isSuccessRequestEndorseTransmittal,
    isLoading: isLoadingRequestEndorseTransmittal,
    isError: isErrorRequestEndorseTransmittal,
    error: errorRequestEndorsementTransmittal,
    reset: resetRequestEndorsementTransmittal,
  } = useRequestEndorseProjectTransmittal();

  const {
    mutate: mutateReleaseForConstruction,
    isSuccess: isSuccessReleaseForConstruction,
    isError: isErrorReleaseForConstruction,
    isLoading: isLoadingReleaseForConstruction,
    error: errorReleaseForConstruction,
    reset: resetReleaseForConstruction,
  } = useReleaseForConstructionProjectTransmittal();

  const {
    mutate: mutateCreateDraftIfNotExist,
    isSuccess: isSuccessCreateDraftIfNotExist,
    isLoading: isLoadingCreateDraftIfNotExist,
    isError: isErrorCreateDraftIfNotExist,
    error: errorCreateDraftIfNotExist,
    reset: resetCreateDraftIfNotExist,
    data: dataCreateDraftIfNotExist,
  } = useCreateDraftProjectTransmittalIfNotExist();

  const {
    checkAnyUnfinishedRequestInMobile,
    onClickSubmitForm,
    mutationUpsertTransmittalItemsAndRevisionsForTables,
    updateCacheFromUpdateTransmittalItemResponse,
    errorKeySeperator,
  } = useSubmitRow(
    transmittalDetails?.masterScheduleId ?? -1,
    id ?? -1,
    transmittalDetails?.type ?? ETransmittalType.DESIGNER,
    useFormMethods,
  );
  const {
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisionsForTables;

  const { loading: isAnyActionLoading, setLoading: setIsAnyActionLoading } = useDeferLoading(
    isLoadingUpsertTransmittalItemsAndRevisions ||
      isLoadingUpdateTransmittal ||
      isLoadingDeleteTransmittal ||
      isLoadingEndorseTransmittal ||
      isLoadingReleaseForConstruction ||
      isLoadingCreateDraftIfNotExist ||
      isPageUploading ||
      isMutatingUpsertTransmittalItems > 0,
    isPageDirty,
  );

  const { data: transmittalPurposeOptions } = useGetTransmittalPurposeOption({
    projectId,
    masterScheduleId: transmittalDetails?.masterScheduleId,
    transmittalId: transmittalDetails?.id,
    transmittalType: transmittalDetails?.type,
  });

  const localTransmittalOptions = React.useMemo(() => {
    if (!transmittalPurposeOptions || transmittalPurposeOptions.length === 0) return [];
    if (isReadOnly) return transmittalPurposeOptions;
    return transmittalPurposeOptions?.filter((tpo) => tpo.isActive);
  }, [transmittalPurposeOptions, isReadOnly]);

  React.useEffect(() => {
    if (!transmittalDetails) {
      setTypeOption(undefined);
      return;
    }

    setTypeOption(
      transmittalDetails.type ? TransmittalTypeOptions.find((tto) => tto.code === transmittalDetails.type) : undefined,
    );
  }, [JSON.stringify(transmittalDetails), JSON.stringify(localTransmittalOptions)]);

  const getDefaultFormValues = (
    isRefetchingLocal: boolean,
    transmittalLocal?: IProjectTransmittalDetail,
    designModificationOptionsLocal?: IProjectDesignModificationOptionForDesignerTransmittal[],
  ) => {
    if (isRefetchingLocal && useFormMethods.formState.defaultValues) return useFormMethods.formState.defaultValues;

    const state: Partial<ITransmittalDetailsFormState> = {
      [ETransmittalDetailsFormKeys.REMARK]: transmittalLocal?.remark ?? null,
      [ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE]:
        transmittalLocal?.purpose.code === ETransmittalPurposeCodeConst.SE ? null : transmittalLocal?.purpose ?? null,
      [ETransmittalDetailsFormKeys.RSE]:
        transmittalLocal?.rseUserProfile && transmittalLocal?.purpose.code === PURPOSE_CODE_Z
          ? [
              {
                id: transmittalLocal.rseUserProfile.id ?? 0,
                displayName: transmittalLocal.rseUserProfile.displayName,
                uid: transmittalLocal.rseUserProfile.uid,
                email: transmittalLocal.rseUserProfile.email,
                upn: transmittalLocal.rseUserProfile.upn,
              },
            ]
          : [],
      [ETransmittalDetailsFormKeys.CHANGE_T3]:
        transmittalLocal?.designModifications?.map((t3) => {
          const found = designModificationOptionsLocal?.find((option) => option.id === t3.id);
          return {
            projectTransmittalDesignModificationRowVersion: t3.projectTransmittalDesignModificationRowVersion,
            projectTransmittalDesignModificationLinkId: t3.projectTransmittalDesignModificationLinkId,
            projectDesignModificationId: t3.id,
            transmittalId: id ?? 0,
            designChangeNo: found?.designChangeNo ?? t3.designChangeNo,
          };
        }) ?? [],
    };

    return state;
  };

  const handleSubmit = React.useCallback(
    (type: 'save' | 'requestEndorsement' | 'endorsement' | 'releaseForConstruction') => async (formData: any) => {
      const { designModifications, ...data } = formData;
      const purposeCodeSelected = data[ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE];
      const isZCode =
        purposeCodeSelected &&
        ((Array.isArray(purposeCodeSelected) &&
          (purposeCodeSelected as ITransmittalPurpose[])?.find((pcs) => pcs.code === PURPOSE_CODE_Z)) ||
          (purposeCodeSelected as ITransmittalPurpose).code === PURPOSE_CODE_Z);
      const rseSelected = (data[ETransmittalDetailsFormKeys.RSE] ?? []) as IUserProfileOption[];
      const rse = rseSelected.length > 0 ? rseSelected[0].id : undefined;

      const designModificationData: IUpsertProjectTransmittalDesignModification[] = (
        (designModifications ?? []) as IUpsertProjectTransmittalDesignModification[]
      ).map((option) => {
        const found = transmittalDetails?.designModifications?.find(
          (existingT3) => existingT3.id === option.projectDesignModificationId,
        );
        return {
          id: found?.id,
          transmittalId: id ?? 0,
          projectTransmittalDesignModificationRowVersion: found?.projectTransmittalDesignModificationRowVersion,
          projectTransmittalDesignModificationLinkId: found?.projectTransmittalDesignModificationLinkId,
          projectDesignModificationId: option.projectDesignModificationId,
          rowVersion: found?.rowVersion,
        };
      });

      const updateRequest: IUpdateProjectTransmittalRequest = {
        projectId: transmittalDetails?.projectId || 0,
        masterScheduleId: transmittalDetails?.masterScheduleId || 0,
        remark: data[ETransmittalDetailsFormKeys.REMARK],
        type: transmittalDetails?.type,
        siteTransmittalId: transmittalDetails?.siteTransmittalId,
        purposeId: data[ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE].id,
        id: transmittalDetails?.id || 0,
        rowVersion: transmittalDetails?.rowVersion || '',
        rseUserProfileId: isZCode ? rse : undefined,
        designModifications: designModificationData,
      };
      if (type === 'save') {
        mutateUpdateTransmittal(updateRequest);
        return;
      }
      if (type === 'endorsement') {
        mutateEndorseTransmittal(updateRequest);
        return;
      }
      if (type === 'requestEndorsement') {
        mutateRequestEndorseTransmittal(updateRequest);
        return;
      }
      if (type === 'releaseForConstruction') {
        mutateReleaseForConstruction(updateRequest);
      }
    },
    [JSON.stringify(transmittalDetails)],
  );

  const onSaveTransmittalItems = React.useCallback(async () => {
    // isXs -> all transmittal item requests should be finished within TransmittalItemTableFormMobileDetailModal
    if (isXs && checkAnyUnfinishedRequestInMobile()) {
      toastMessage(
        <ToastMessage
          message="Found unfinished updates in transmittal items. Please finish updates before save."
          toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
        />,
      );
      return false;
    }

    const result = isXs ? true : await onClickSubmitForm(transmittalItemTableEditingRef);
    return result;
  }, [isXs]);

  const onClickPrepareApprovalOrReview = (transmittalTypeToCreate: ETransmittalType) => {
    if (!transmittalDetails?.masterScheduleId) return;
    const createRequest: ICreateProjectTransmittalRequest = {
      projectId,
      masterScheduleId: transmittalDetails?.masterScheduleId,
      type: transmittalTypeToCreate,
      siteTransmittalId: id,
    };
    mutateCreateDraftIfNotExist(createRequest);
  };
  const onClickViewHistory = () => {
    const params = {
      id: (transmittalDetails?.id || 0).toString(),
      masterScheduleId: (transmittalDetails?.masterScheduleId || 0).toString(),
    };
    if (transmittalDetails?.type === ETransmittalType.DESIGNER) {
      navigate(routes.transmittals.designerTransmittal.history.path, params);
    } else if (transmittalDetails?.type === ETransmittalType.SITE) {
      navigate(routes.transmittals.siteTransmittal.history.path, params);
    } else if (transmittalDetails?.type === ETransmittalType.ICE) {
      navigate(routes.transmittals.checkerTransmittal.history.path, params);
    } else if (transmittalDetails?.type === ETransmittalType.REVIEWER) {
      navigate(routes.transmittals.lirTransmittal.history.path, params);
    }
  };

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const items: IBreadcrumbItem[] = [
      {
        text: `${projectNo} - Home`,
        prefixIconName: 'House',
        onClick: () => {
          navigate(routes.t1.path);
        },
      },
      {
        text: `Search ${transmittalName}`,
        prefixIconName: 'Search' as IconName,
        onClick: () => {
          if (transmittalDetails?.type === ETransmittalType.DESIGNER) {
            navigate(routes.transmittals.designerTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.SITE) {
            navigate(routes.transmittals.siteTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.ICE) {
            navigate(routes.transmittals.checkerTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.REVIEWER) {
            navigate(routes.transmittals.lirTransmittal.path);
          }
        },
      },
    ];
    if (transmittalDetails?.isArchived)
      return [
        ...items,
        {
          text: `${mode} Lambeth Internal Review`,
          active: true,
          prefixIconName: 'JournalText' as IconName,
        },
      ];

    return [
      ...items,
      {
        text: `${mode} ${transmittalName}`,
        active: true,
        prefixIconName: 'JournalText' as IconName,
      },
    ];
  }, [id, JSON.stringify(transmittalDetails), transmittalName]);

  const topBarLeftItems: IDetailPageTopBarItem[] = React.useMemo(() => {
    if (!transmittalDetails) return [] as IDetailPageTopBarItem[];

    const releaseForConstructionBtn = {
      title: 'Release for Construction',
      buttonIconName: 'Building' as IconName,
      onClick: (e: any) => {
        openConfirmationPortal(e, ETransmittalConfirmationActionType.releaseForConstruction);
      },
      componentType: ComponentType.Success,
      type: EDetailPageTopBarItemType.loadingButton,
      isError: isErrorReleaseForConstruction,
      isLoading: isLoadingReleaseForConstruction,
      isSuccess: isSuccessReleaseForConstruction,
      disable: isAnyActionLoading,
    };

    if (
      transmittalDetails.type === ETransmittalType.DESIGNER &&
      transmittalDetails.status === ETransmittalStatus.APPROVED
    ) {
      return isEnableReleaseForConstruction ? [releaseForConstructionBtn] : [];
    }

    if (
      transmittalDetails.type === ETransmittalType.SITE &&
      transmittalDetails.status === ETransmittalStatus.APPROVED
    ) {
      const btns = [];
      if (isEnablePrepareApproval)
        btns.push({
          type: EDetailPageTopBarItemType.loadingButton,
          componentType: ComponentType.Primary,
          isError:
            isErrorCreateDraftIfNotExist &&
            confirmationActionType === ETransmittalConfirmationActionType.prepareApproval,
          isLoading:
            isLoadingCreateDraftIfNotExist &&
            confirmationActionType === ETransmittalConfirmationActionType.prepareApproval,
          isSuccess:
            isSuccessCreateDraftIfNotExist &&
            confirmationActionType === ETransmittalConfirmationActionType.prepareApproval,

          title: 'Prepare Approval',
          buttonIconName: 'Journal' as IconName,
          onClick: (e: any) => {
            openConfirmationPortal(e, ETransmittalConfirmationActionType.prepareApproval);
          },
        });
      if (isEnablePrepareReview)
        btns.push({
          type: EDetailPageTopBarItemType.loadingButton,
          componentType: ComponentType.Primary,
          isError:
            isErrorCreateDraftIfNotExist && confirmationActionType === ETransmittalConfirmationActionType.prepareReview,
          isLoading:
            isLoadingCreateDraftIfNotExist &&
            confirmationActionType === ETransmittalConfirmationActionType.prepareReview,
          isSuccess:
            isSuccessCreateDraftIfNotExist &&
            confirmationActionType === ETransmittalConfirmationActionType.prepareReview,
          title: 'Prepare Review',
          buttonIconName: 'Journal' as IconName,
          onClick: (e: any) => {
            openConfirmationPortal(e, ETransmittalConfirmationActionType.prepareReview);
          },
        });

      if (isEnableReleaseForConstruction) btns.push(releaseForConstructionBtn);
      return btns;
    }
    if (transmittalDetails.type === ETransmittalType.ICE && transmittalDetails.status === ETransmittalStatus.APPROVED) {
      return isEnableReleaseForConstruction ? [releaseForConstructionBtn] : [];
    }

    const defaultDraftButtons: IDetailPageTopBarItem[] = [];

    if (isEnableSaveTransmittal)
      defaultDraftButtons.push({
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError:
          confirmationActionType === ETransmittalConfirmationActionType.save &&
          (isErrorUpdateTransmittal || isErrorUpsertTransmittalItemsAndRevisions),
        isLoading:
          confirmationActionType === ETransmittalConfirmationActionType.save &&
          (isLoadingUpdateTransmittal || isLoadingUpsertTransmittalItemsAndRevisions),
        isSuccess:
          confirmationActionType === ETransmittalConfirmationActionType.save &&
          isSuccessUpdateTransmittal &&
          isSuccessUpsertTransmittalItemsAndRevisions,
        title: 'Save',
        buttonIconName: 'FolderCheck' as IconName,
        onClick: async (e: any) => {
          openConfirmationPortal(e, ETransmittalConfirmationActionType.save, useFormMethods);
        },
        showNoneInReadOnly: true,
        disable: isAnyActionLoading || !isTransmittalItemTableSubmitFormEnabled,
      });
    if (isEnableDeleteTransmittal)
      defaultDraftButtons.push({
        title: 'Delete',
        buttonIconName: 'FileExcel' as IconName,
        onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          openConfirmationPortal(e, ETransmittalConfirmationActionType.delete);
        },
        componentType: ComponentType.Danger,
        type: EDetailPageTopBarItemType.loadingButton,
        isError: isErrorDeleteTransmittal,
        isLoading: isLoadingDeleteTransmittal,
        isSuccess: isSuccessDeleteTransmittal,
        showNoneInReadOnly: true,
        disable: isAnyActionLoading,
      });

    if (transmittalDetails.status === ETransmittalStatus.DRAFT) {
      if (transmittalDetails.type === ETransmittalType.ICE || transmittalDetails.type === ETransmittalType.REVIEWER)
        return [
          ...defaultDraftButtons,
          ...(isEnableEndorseTransmittal
            ? [
                {
                  buttonIconName: 'EnvelopeCheck' as IconName,
                  type: EDetailPageTopBarItemType.loadingButton,
                  componentType: ComponentType.Success,
                  isError: isErrorEndorseTransmittal,
                  isLoading: isLoadingEndorseTransmittal,
                  isSuccess: isSuccessEndorseTransmittal,
                  title: 'Sign and Send',
                  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    openConfirmationPortal(e, ETransmittalConfirmationActionType.signAndSend);
                  },
                  showNoneInReadOnly: true,
                  disable: isAnyActionLoading,
                },
              ]
            : []),
        ];

      const draftButtons: IDetailPageTopBarItem[] = [...defaultDraftButtons];

      if (isEnableRequestEndorsement)
        draftButtons.push({
          title: 'Request Endorsement',
          buttonIconName: 'EnvelopeCheck' as IconName,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            openConfirmationPortal(e, ETransmittalConfirmationActionType.requestEndorsement);
          },
          componentType: ComponentType.Secondary,
          type: EDetailPageTopBarItemType.loadingButton,
          isError: isErrorRequestEndorseTransmittal,
          isLoading: isLoadingRequestEndorseTransmittal,
          isSuccess: isSuccessRequestEndorseTransmittal,
          disable: isAnyActionLoading,
        });

      if (isEnableEndorseTransmittal) {
        draftButtons.push({
          title: 'Endorse Transmittal',
          buttonIconName: 'Pen' as IconName,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            openConfirmationPortal(e, ETransmittalConfirmationActionType.endorse, useFormMethods);
          },
          componentType: ComponentType.Success,
          type: EDetailPageTopBarItemType.loadingButton,
          isError:
            confirmationActionType === ETransmittalConfirmationActionType.endorse &&
            (isErrorEndorseTransmittal || isErrorUpsertTransmittalItemsAndRevisions),
          isLoading:
            confirmationActionType === ETransmittalConfirmationActionType.endorse &&
            (isLoadingEndorseTransmittal || isLoadingUpsertTransmittalItemsAndRevisions),
          isSuccess:
            confirmationActionType === ETransmittalConfirmationActionType.endorse &&
            isSuccessEndorseTransmittal &&
            isSuccessUpsertTransmittalItemsAndRevisions,
          disable: isAnyActionLoading || !isTransmittalItemTableSubmitFormEnabled,
        });
      }
      return draftButtons;
    }

    return [] as IDetailPageTopBarItem[];
  }, [
    isAnyActionLoading,
    transmittalDetails?.type,
    transmittalDetails?.status,
    isErrorUpsertTransmittalItemsAndRevisions,
    isLoadingUpsertTransmittalItemsAndRevisions,
    isSuccessUpsertTransmittalItemsAndRevisions,
    isErrorUpdateTransmittal,
    isSuccessUpdateTransmittal,
    isLoadingUpdateTransmittal,
    isErrorDeleteTransmittal,
    isSuccessDeleteTransmittal,
    isLoadingDeleteTransmittal,
    isErrorEndorseTransmittal,
    isLoadingEndorseTransmittal,
    isSuccessEndorseTransmittal,
    isErrorRequestEndorseTransmittal,
    isLoadingRequestEndorseTransmittal,
    isSuccessRequestEndorseTransmittal,
    JSON.stringify(transmittalDetails),
    isErrorReleaseForConstruction,
    isSuccessReleaseForConstruction,
    isLoadingReleaseForConstruction,
    isSuccessCreateDraftIfNotExist,
    isLoadingCreateDraftIfNotExist,
    isErrorCreateDraftIfNotExist,
    JSON.stringify(topBtnsVisibility),
    isTransmittalItemTableSubmitFormEnabled,
    openConfirmationPortal,
    handleSubmit,
    onSaveTransmittalItems,
  ]);

  const topBarRightItems: IDetailPageTopBarItem[] = React.useMemo(() => {
    return [
      {
        title: 'View History',
        onClick: onClickViewHistory,
        componentType: ComponentType.Secondary,
        type: EDetailPageTopBarItemType.button,
        // onRemoveTag,
        // onAddTag,
        disabled: isAnyActionLoading,
        showNoneInArchivedProject: false,
      } as IDetailPageTopBarItem,
    ];
  }, [JSON.stringify(transmittalDetails), isAnyActionLoading]);

  const notes: INoteItem<React.ReactElement<typeof Icon>>[] = React.useMemo(() => {
    const tempNotes: INoteItem<React.ReactElement<typeof Icon>>[] = [];

    if (!transmittalDetails) return tempNotes;

    if (isSuperseded && latestTransmittal)
      tempNotes.push({
        componentType: ComponentType.Danger,
        prefixIcon: 'ExclamationTriangle',
        note: (
          <>
            This Transmittal has been superseded. Please see{' '}
            <span
              className="gcl-fl-md"
              onClick={() => {
                navigate(getTransmittalItemRoute(latestTransmittal.type), { id: latestTransmittal.id });
              }}
            >
              {latestTransmittal.transmittalNo}
            </span>{' '}
            for the latest information.
          </>
        ),
      });

    switch (transmittalDetails.type) {
      case ETransmittalType.DESIGNER:
        tempNotes.push({
          componentType: ComponentType.Primary,
          prefixIcon: 'ExclamationCircle',
          note: (
            <>
              To allow TWC to release for construction without further checking process, please set the purpose code to
              Z (Approved by Authorities). This option should only be selected if the design has already been checked to
              the standard required by Gammon&apos;s PDS/05 procedure.
            </>
          ),
        });
        break;

      case ETransmittalType.SITE:
        if (transmittalDetails.inverseCheckerTransmittal) {
          const relativeCt = transmittalDetails.inverseCheckerTransmittal.find(
            (ct) => ct.status === ETransmittalStatus.APPROVED,
          );
          if (relativeCt) {
            tempNotes.push({
              componentType: ComponentType.Warning,
              prefixIcon: 'Lightbulb',
              note: (
                <>
                  There is a related checker transmittal. Please see{' '}
                  <span
                    className="gcl-fl-md"
                    onClick={() => {
                      navigate(routes.transmittals.checkerTransmittal.item.path, { id: relativeCt.id });
                    }}
                  >
                    {relativeCt.transmittalNo}
                  </span>{' '}
                  for information.
                </>
              ),
            });
          }
        }
        break;

      case ETransmittalType.ICE: {
        if (transmittalDetails.siteTransmittal)
          tempNotes.push({
            componentType: ComponentType.Warning,
            prefixIcon: 'Lightbulb',
            note: (
              <>
                There is a related site transmittal. Please see{' '}
                <span
                  className="gcl-fl-md"
                  onClick={() => {
                    navigate(routes.transmittals.siteTransmittal.item.path, {
                      id: transmittalDetails.siteTransmittalId,
                    });
                  }}
                >
                  {transmittalDetails.siteTransmittal.transmittalNo}
                </span>{' '}
                for information.
              </>
            ),
          });

        if (!transmittalDetails.inverseCheckerTransmittal || transmittalDetails.inverseCheckerTransmittal.length <= 0) {
          if (masterSchedule?.responsibleReviewer)
            tempNotes.push({
              componentType: ComponentType.Danger,
              prefixIcon: 'ExclamationCircle',
              note: 'Reviewer is assigned for this T1 package. Please create a reviewer transmittal and get approved before Release for Construction.',
            });
        } else {
          const draftLir = transmittalDetails.inverseCheckerTransmittal.find(
            (lir) => lir.status === ETransmittalStatus.DRAFT,
          );
          if (draftLir) {
            tempNotes.push({
              componentType: ComponentType.Danger,
              prefixIcon: 'ExclamationCircle',
              note: (
                <>
                  There is a related reviewer transmittal (
                  <span
                    className="gcl-fl-md"
                    onClick={() => {
                      navigate(routes.transmittals.lirTransmittal.item.path, { id: draftLir.id });
                    }}
                  >
                    {draftLir.transmittalNo}
                  </span>
                  ) pending. Approval from the transmittal is needed before Release for Construction.
                </>
              ),
            });
          }
        }

        if (!isAnyIceDesignCertified) {
          tempNotes.push({
            componentType: ComponentType.Danger,
            prefixIcon: 'ExclamationCircle',
            note: <>No Transmittal item is approved.</>,
          });
        }

        if (dataCheckerTransmittalsByT1?.find((ice) => ice.status === ETransmittalStatus.DRAFT && ice.id !== id)) {
          tempNotes.push({
            componentType: ComponentType.Danger,
            prefixIcon: 'ExclamationCircle',
            note: (
              <>
                This package contains more than one draft Approval. Please delete all drafts except the one you wish to
                approve
              </>
            ),
          });
        }
        break;
      }
      case ETransmittalType.REVIEWER:
        if (transmittalDetails.siteTransmittal)
          tempNotes.push({
            componentType: ComponentType.Warning,
            prefixIcon: 'Lightbulb',
            note: (
              <>
                There is a related site transmittal. Please see{' '}
                <span
                  className="gcl-fl-md"
                  onClick={() => {
                    navigate(routes.transmittals.siteTransmittal.item.path, {
                      id: transmittalDetails.siteTransmittalId,
                    });
                  }}
                >
                  {transmittalDetails.siteTransmittal.transmittalNo}
                </span>{' '}
                for information.
              </>
            ),
          });
        tempNotes.push({
          componentType: ComponentType.Warning,
          prefixIcon: 'Lightbulb',
          note: 'Lambeth Independent Review is for unofficial review only',
        });
        tempNotes.push({
          componentType: ComponentType.Danger,
          prefixIcon: 'ExclamationCircle',
          note: (
            <>
              Lambeth Independent Review (LIR) is the replacement procedure for the original T2 for review. Get the
              instruction{' '}
              <a href="/pdf/LIRManual.pdf" target="_blank" rel="noopener noreferrer">
                here for
              </a>{' '}
              or <a href="mailto:gems@gammonconstruction.com?Subject=Lambeth Independent Review">contact us</a> for
              details.
            </>
          ),
        });
        break;
      default:
        break;
    }

    if (transmittalDetails.status === ETransmittalStatus.APPROVED && transmittalDetails.twcReleased)
      tempNotes.push({
        componentType: ComponentType.Success,
        prefixIcon: 'CheckCircle',
        note: (
          <>
            Released for Construction by {transmittalDetails.twcProjectUserProfile?.userProfile?.displayName} on{' '}
            {convertDateToString(convertISOStringToDate(transmittalDetails.twcReleasedDate || ''))}
          </>
        ),
      });
    return tempNotes;
  }, [JSON.stringify(transmittalDetails), JSON.stringify(latestTransmittal), isSuperseded, isAnyIceDesignCertified]);

  const tags: ITagViewItem[] = React.useMemo(() => {
    const tempArray: ITagViewItem[] = [];
    if (!transmittalDetails) return tempArray;

    if (transmittalDetails.sendDate)
      tempArray.push({
        id: 1,
        name: 'Issue Date',
        tagTypeId: 0,
        tagTypeName: '',
        disableRemove: false,
        componentType: ComponentType.Info,
        rowVersion: transmittalDetails.rowVersion,
        onRenderTagContent: (item: ITag | undefined) => {
          return <>{convertISOStringToLocalDateString(transmittalDetails?.sendDate)}</>;
        },
      });

    if (transmittalDetails.sendFromProjectCompany && transmittalDetails.sendToProjectCompany)
      tempArray.push({
        id: 2,
        name: 'From-To',
        tagTypeId: 0,
        tagTypeName: '',
        disableRemove: false,
        componentType: ComponentType.Info,
        onRenderTagContent: (item: ITag | undefined) => {
          return (
            <>
              {transmittalDetails.sendFromProjectCompany?.companyTypeId === ECompanyTypeIdConst.PLACEHOLDER
                ? ''
                : transmittalDetails?.sendFromProjectCompany?.name}
              <Icon iconName="ArrowRight" className="gcl-mx-xs" />
              {transmittalDetails.sendToProjectCompany?.companyTypeId === ECompanyTypeIdConst.PLACEHOLDER
                ? ''
                : transmittalDetails?.sendToProjectCompany?.name}
            </>
          );
        },
        rowVersion: '', // TODO put it actually rowversion
      });

    return tempArray;
  }, [JSON.stringify(transmittalDetails)]);

  const { localTags, onRemove: onRemoveTag, onAdd: onAddTag } = useLocalTags(tags);

  const isRefetching =
    isRefetchingTransmittalDetails ||
    isRefetchingGetMasterSchedule ||
    isRefetchingTransmittalItemDocumentTypes ||
    isRefetchingDistribution;
  return (
    <Scrollbar>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
          },
          {
            isSuccess: isSuccessUpdateTransmittal,
            message: 'Update Success',
            reset: () => {
              resetUpdateTransmittal();
              resetUpsertTransmittalItemsAndRevisions();
            },
          },
          {
            isSuccess: isSuccessEndorseTransmittal,
            message: 'Endorse Success',
            reset: resetEndorseTransmittal,
          },
          {
            isSuccess: isSuccessRequestEndorseTransmittal,
            message: 'Request Endorsement Success',
            reset: resetRequestEndorsementTransmittal,
          },
          {
            isSuccess: isSuccessReleaseForConstruction,
            message: 'Release for Construction Success',
            reset: resetReleaseForConstruction,
          },
          {
            isSuccess: isSuccessDeleteTransmittal,
            message: 'Delete Transmittal Success',
          },
          {
            isSuccess: isSuccessCreateDraftIfNotExist,
            message:
              confirmationActionType === ETransmittalConfirmationActionType.prepareApproval
                ? 'Prepare Approval Success'
                : 'Prepare Review Success',
            reset: resetCreateDraftIfNotExist,
          },
        ]}
        callbackAfterToasted={() => {
          if (isSuccessUpsertTransmittalItemsAndRevisions) {
            if (isSuccessUpdateTransmittal || isSuccessEndorseTransmittal) {
              useFormMethods.reset();
              queryClient.invalidateQueries([projectId, id, FETCH_QUERIES_NAME.TRANSMITTAL]);
              return;
            }
            updateCacheFromUpdateTransmittalItemResponse(dataUpsertTransmittalItemsAndRevisions?.items);

            const tables = (useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS) ??
              []) as IFormFileDrawingItem[][];
            Object.entries(transmittalItemTableEditingRef.current).map(([tableIndexStr, tableEvents]) => {
              const { queryKey, tableRef } = tableEvents as ITransmittalItemTableEditingEvents;
              const tableIndex = parseInt(tableIndexStr, 10);
              const tablePrefix = getTablePrefix(tableIndex);
              const table = useFormMethods.getValues(tablePrefix) as IFormFileDrawingItem[];
              const newTable = table?.map((row) => {
                return {
                  ...row,
                  file: undefined,
                  form: {
                    ...row.form,
                    projectFile: undefined,
                    lastModifiedDateTime: undefined,
                  },
                } as IFormFileDrawingItem;
              });
              tables[tableIndex] = newTable;
              queryClient.invalidateQueries(queryKey);

              Object.entries(tableRef.current).map(([rowIndexStr, rowEvents]) => {
                const { setRowState } = rowEvents as ITransmittalItemRowEditingEvents;
                if (setRowState) setRowState('Idle');
              });
            });
            useFormMethods.setValue(ETransmittalDetailsFormKeys.DOCUMENTS, tables);

            return;
          }

          queryClient.invalidateQueries([projectId, id, FETCH_QUERIES_NAME.TRANSMITTAL]);
          if (isSuccessDeleteTransmittal) {
            setTimeout(() => {
              navigate(routes.transmittals.list.path);
            }, 0);
            return;
          }
          if (isSuccessCreateDraftIfNotExist) {
            useFormMethods.reset({}, { keepDirty: false });
            setTimeout(() => {
              navigate(getTransmittalItemRoute(dataCreateDraftIfNotExist.item?.type), {
                id: dataCreateDraftIfNotExist?.item?.id?.toString(),
              });
            }, 0);
          }
          if (
            (isSuccessEndorseTransmittal && transmittalDetails?.type === ETransmittalType.DESIGNER) ||
            (isSuccessReleaseForConstruction && transmittalDetails?.type === ETransmittalType.ICE)
          ) {
            queryClient.invalidateQueries([projectId, masterSchedule?.id, FETCH_QUERIES_NAME.CHECK_CERTIFICATES_BY_T1]);
          }

          useFormMethods.reset(useFormMethods.getValues());
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          {
            error: errorUpdateTransmittal,
            genericError: 'Update transmittal fail',
            reset: () => {
              resetUpdateTransmittal();
              resetUpsertTransmittalItemsAndRevisions();
            },
          },
          {
            error: errorUpsertTransmittalItemsAndRevisions,
            genericError: 'Update transmittal items fail',
            reset: resetUpsertTransmittalItemsAndRevisions,
            keyExtractor: errorKeySeperator,
          },
          { error: errorDeleteTransmittal, genericError: 'Delete Transmittal Fail', reset: resetDeleteTransmittal },
          {
            error: errorRequestEndorsementTransmittal,
            genericError: 'Request Endorsement Fail',
            reset: resetRequestEndorsementTransmittal,
          },
          { error: errorEndorseTransmittal, genericError: 'Endorse Transmittal Fail', reset: resetEndorseTransmittal },
          {
            error: errorReleaseForConstruction,
            genericError: 'Release for Construction Fail',
            reset: resetReleaseForConstruction,
          },
          {
            error: errorCreateDraftIfNotExist,
            genericError:
              confirmationActionType === ETransmittalConfirmationActionType.prepareApproval
                ? 'Prepare Approval Fail'
                : 'Prepare Review Fail',
            reset: resetCreateDraftIfNotExist,
          },
          { error: errorCheckerTransmittalsByT1, genericError: 'Get checker transmittals by T1 failed' },
          { error: errorDesignModificationOptions, genericError: 'Get design modification options failed' },
          { error: errorGetTransmittalDetails, genericError: 'Get transmittal failed' },
          { error: errorGetMasterSchedule, genericError: 'Get master schedule failed' },
          { error: errorTransmittalItemDocumentTypes, genericError: 'Get transmittal item document types failed' },
          { error: errorLatestTransmittal, genericError: 'Get latest transmittal failed' },
          { error: errorDistribution, genericError: 'Get distribution failed' },
        ]}
        callbackAfterToast={() => {
          if (errorUpsertTransmittalItemsAndRevisions) {
            Object.entries(transmittalItemTableEditingRef.current).map(([tableIndex, tableEvents]) => {
              const { tableRef } = tableEvents as ITransmittalItemTableEditingEvents;
              Object.entries(tableRef.current).map(([rowIndex, rowEvents]) => {
                if (rowEvents.setRowState) rowEvents.setRowState('Editing');
              });
            });
          }
        }}
      />
      <DetailPageLayout
        errorGetDetail={errorGetTransmittalDetails}
        isRestricted={isRestricted}
        data={mode === 'Create' ? {} : transmittalDetails}
        isRefetching={isRefetching}
        isLoading={
          isLoadingTransmittalDetails ||
          isLoadingGetMasterSchedule ||
          isLoadingTransmittalItemDocumentTypes ||
          isLoadingDistribution
        }
        rightMenu={(setIsDrawerOpen) => (
          <DetailPageRightMenu
            setIsDrawerOpen={setIsDrawerOpen}
            type={EDetailPageRightMenuGeneralInfoType.Transmittal}
            masterSchedule={masterSchedule}
          />
        )}
        title={createDefaultDetailPageTitleFn(
          <>
            {!transmittalDetails?.isArchived && (
              <span className="gcl-my-sm">{`${transmittalName}${
                mode !== 'Create' && transmittalDetails?.transmittalNo ? ` - ${transmittalDetails?.transmittalNo}` : ''
              }`}</span>
            )}
            {transmittalDetails?.isArchived && (
              <span>
                Lambeth Internal Review - {transmittalDetails?.transmittalNo}
                <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                  Archived
                </Badge>
              </span>
            )}
            <span>
              <TagView tags={localTags} onRemove={onRemoveTag} onAdd={onAddTag} disabled />
            </span>
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={() => {
          return (
            <Tabs>
              <Tab headerText="Details">
                <>
                  <DetailPageTopBar leftItems={topBarLeftItems} rightItems={topBarRightItems} isReadOnly={isReadOnly} />
                  <Form
                    defaultValues={{
                      callback: () => {
                        const state = getDefaultFormValues(isRefetching, transmittalDetails, designModificationOptions);
                        setDefaultValues((old) => {
                          return { ...old, ...state };
                        });
                        return { ...defaultValues, ...state };
                      },
                      deps: [transmittalDetails, designModificationOptions, isRefetching],
                    }}
                    enableUnsavedChangesPrompt
                    unsavedChangesPromptProps={{
                      usePrompt,
                    }}
                    useFormMethods={useFormMethods}
                    render={(formFieldMethods) => {
                      return (
                        <TransmittalDetailTabDetail
                          setDefaultValues={setDefaultValues}
                          isWindowFocus={isWindowFocus}
                          transmittalItemTableEditingRef={transmittalItemTableEditingRef}
                          isPageDirty={isPageDirty}
                          setRowTableIsEditing={setRowTableIsEditing}
                          setRowTableIsUploading={setRowTableIsUploading}
                          setIsAnyActionLoading={setIsAnyActionLoading}
                          setTableIsIceDesignCertified={setTableIsIceDesignCertified}
                          isAnyActionLoading={isAnyActionLoading}
                          formFieldMethods={formFieldMethods}
                          shouldShowRemovedFromUse={shouldShowRemovedFromUse}
                          transmittalOptions={localTransmittalOptions}
                          transmittalItemDocumentTypes={transmittalItemDocumentTypes ?? []}
                          projectId={projectId}
                          isReadOnly={isReadOnly}
                          renderExportExcelButton={(excelIcon) => {
                            return (
                              <DownloadButton
                                exportFunction={exportTransmittalDetailExcelAsync}
                                defaultExportProps={{
                                  projectId,
                                  projectTransmittalId: Number(transmittalDetails?.id) || 0,
                                  projectMasterScheduleId: Number(transmittalDetails?.masterScheduleId) || 0,
                                  transmittalType: String(transmittalDetails?.type) || '',
                                  shouldShowRemovedFromUse,
                                }}
                                disabled={isAnyActionLoading}
                                buttonRender={(exportFile) => <div onClick={() => exportFile()}>{excelIcon}</div>}
                              />
                            );
                          }}
                          renderExportPdfButton={(pdfIcon) => (
                            <DownloadButton
                              exportFunction={exportTransmittalDetailPdfAsync}
                              defaultExportProps={{
                                projectId,
                                projectTransmittalId: Number(transmittalDetails?.id) || 0,
                              }}
                              disabled={isAnyActionLoading}
                              buttonRender={(exportFile) => <div onClick={() => exportFile()}>{pdfIcon}</div>}
                            />
                          )}
                          transmittalId={transmittalDetails?.id}
                          transmittalType={transmittalDetails?.type}
                          masterScheduleId={transmittalDetails?.masterScheduleId || 0}
                          onSearch={(searchText) => {}}
                          onClickShowHideRemovedItems={(shouldShow) => {
                            setShouldShowRemovedFromUse(shouldShow);
                          }}
                          useFormMethods={useFormMethods}
                          transmittalDetails={transmittalDetails}
                          notes={notes}
                          distributionData={distributionData}
                          designModificationOptions={designModificationOptions}
                          hasDeletePermission={isEnableDeleteTransmittal}
                        />
                      );
                    }}
                  />
                </>
              </Tab>
              <Tab headerText="Supplementary Info">
                <Attachments<IProjectFileAttachmentsTransmittalsRequest>
                  id={`supplementary-info-${supplementaryFileType}`}
                  useFetchFilesQueryKey={[
                    projectId,
                    id,
                    FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_TRANSMITTALS,
                    supplementaryFileType,
                  ]}
                  fileType={supplementaryFileType}
                  mode={supplementaryMode}
                  useFetchFiles={useFetchAttachmentTransmittal}
                  useFetchFilesProps={{ transmittalId: id ?? 0, fileType: supplementaryFileType, projectId }}
                  headerComponent={
                    <Note
                      className="gcl-mb-md"
                      componentType={ComponentType.Primary}
                      prefixIcon={<Icon iconName="ExclamationCircle" />}
                    >
                      {`Do not upload the submission drawings to 'Supplementary Files'. Please use the transmittal grid.`}
                    </Note>
                  }
                  uploadFileRequestParam={{ transmittalId: id }}
                />
              </Tab>
              {(canCreateComment || canReplyComment || canViewComment) && (
                <Tab headerText="Comment">
                  <DiscussionList
                    projectTransmittalId={id}
                    formType={EProjectDiscussionFormType.TRANSMITTAL}
                    fileFormType={EProjectFileType.TRANSMITTAL_DISCUSSION_ATTACHMENT}
                    discussionFileType={EProjectDiscussionFileType.TRANSMITTAL}
                    canComment={canCreateComment}
                    canResponse={canReplyComment}
                    canViewDraftComment={canViewDraftComment}
                  />
                </Tab>
              )}
              {(transmittalDetails?.project_LambethReviewerResponse?.length ?? 0) > 0 && (
                <Tab headerText="Review Response">
                  <Accordion
                    content={(transmittalDetails?.project_LambethReviewerResponse ?? [])?.map((r, index) => {
                      return {
                        id: r.id,
                        defaultIsExpanded: index === 0,
                        onRenderBody: () => {
                          return <TransmittalDetailTabReviewResponse lambethReviewerResponse={r} />;
                        },
                        onRenderHeader: () => {
                          return <b>{r.reviewNo}</b>;
                        },
                        onRenderHeaderButtons: () => {
                          return [
                            <Badge
                              key={`transmittal-lrr-header-${index}-status`}
                              type={getLambethReviewerResponseStatusComponentType(r.status)}
                            >
                              {r.status}
                            </Badge>,
                            <span key={`transmittal-lrr-header-${index}-create-date`}>
                              {convertDateToString(convertISOStringToDate(r.createdDateTime))}
                            </span>,
                          ];
                        },
                      };
                    })}
                  />
                </Tab>
              )}
            </Tabs>
          );
        }}
      />
      {renderConfirmationPortal(
        isLoadingDeleteTransmittal ||
          isLoadingEndorseTransmittal ||
          isLoadingRequestEndorseTransmittal ||
          isLoadingReleaseForConstruction,
        isErrorDeleteTransmittal ||
          isErrorEndorseTransmittal ||
          isErrorRequestEndorseTransmittal ||
          isErrorReleaseForConstruction,
        isSuccessDeleteTransmittal ||
          isSuccessEndorseTransmittal ||
          isSuccessRequestEndorseTransmittal ||
          isSuccessReleaseForConstruction,
      )}
    </Scrollbar>
  );
};
